import {ref} from 'vue'
import {defineStore} from 'pinia'
import {useCrud} from "@/composables/CrudAxios";
import type {chosenTable, date, time} from "@/types/types";
import {useRoute, useRouter} from "vue-router";
import {addMinutes} from "@/composables/Utils";
import {useToast} from "primevue/usetoast";

export const useTableStore = defineStore('table', () => {
    const availableTimes = ref([] as time[]);
    const availableDates = ref([] as date[]);
    const chosenTable = ref({} as chosenTable);
    const markers = ref([] as any[]); // marker elements: { date: Date, type: 'line'|'circle', tooltip?: any, disabled?: boolean }
    const eventsByDate = ref({} as Record<string, any[]>); // key: 'dd.mm.YYYY' -> array of events
    const selectedEvent = ref<any | null>(null); // currently selected event (from calendar)
    // Event selections on Contact page
    const selectedTicketCount = ref<any>(null); // for type 1
    const selectedSeatOptionId = ref<any>(null); // for type 2
    const route = useRoute();
    const toast = useToast();
    const router = useRouter();

    async function getDays() {
        const {getAsync} = useCrud();
        try {
            const response = await getAsync(`/extern/table/getdays/?store=${route.query.store}`);

            if (response.status === 403) {
                await router.push({
                    path: '/forbidden',
                    query: {
                        store: route.query.store
                    }
                });
                availableDates.value = [];
            } else {
                availableDates.value = response;
            }

        } catch (err) {
            console.log(err);
            availableDates.value = [];
        }
    }

    async function getEventDays() {
        const {getAsync} = useCrud();
        try {
            const response = await getAsync(`/extern/table/getevents/?store=${route.query.store}`);
            if (!response || (Array.isArray(response) && response.length === 0)) return;
            
            // Clear previous event data to prevent duplicates when navigating back
            eventsByDate.value = {};
            // Remove existing event markers (type 'circle') but keep other markers (e.g., 'line' for closed days)
            markers.value = markers.value.filter((m: any) => m.type !== 'circle');
            
            // Group events by date and track which dates already have markers
            const markedDates = new Set<string>();
            (response as any[]).forEach((ev: any) => {
                if (!ev?.date) return;
                const dateStr: string = ev.date as string; // 'dd.mm.YYYY'
                try {
                    // Only add one marker per date (even if multiple events)
                    if (!markedDates.has(dateStr)) {
                        markedDates.add(dateStr);
                        markers.value.push({
                            date: new Date((dateStr as string).split('.').reverse() as any),
                            type: 'circle',
                            tooltip: [{text: 'Event(s) an diesem Tag', color: ''}],
                        });
                    }
                } catch (e) {
                    // ignore parse issues
                }
                // Store all events per date in an array
                if (!eventsByDate.value[dateStr]) {
                    eventsByDate.value[dateStr] = [];
                }
                eventsByDate.value[dateStr].push(ev);
            });
        } catch (e) {
            console.log(e);
        }
    }

    async function getTimes(date: Date, persons: number) {
        const {getAsync} = useCrud();

        const options: any = {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
        };

        let response = await getAsync(`/extern/table/gettimes/?store=${route.query.store}&viewdate=${date.toLocaleString("de-DE", options)}&persons=${persons}`);
        if (response.length < 1) {
            availableTimes.value = [];
            
            // Check if there is an event on this day before showing an error
            const dateStr = date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
            const hasEvent = Array.isArray(eventsByDate.value[dateStr]) && eventsByDate.value[dateStr].length > 0;
            
            if (!hasEvent) {
                toast.add({ severity:  'error', summary: 'Keine Zeiten für den ausgewählten Tag verfügbar.', life: 5000 });
            }
            return;
        }
        availableTimes.value = response.map((element: string) => {
            return {time: element, disabled: false} as time;
        })

        const latestTimeSlot = availableTimes.value.at(-1)?.time;
        const firstTimeSlot = availableTimes.value.at(0)?.time;
        const [latestSlot_hours, latestSlot_minutes]: any = latestTimeSlot?.split(':');
        const [firstSlot_hours, firstSlot_minutes]: any = firstTimeSlot?.split(':');
        const startDate = new Date(new Date().setHours(firstSlot_hours, firstSlot_minutes, 0));
        const endDate = new Date(new Date().setHours(latestSlot_hours, latestSlot_minutes, 0));

        for (let i = startDate; i <= endDate; i = addMinutes(i, 15)) {
            let index = availableTimes.value.findIndex(object => object.time === i.toLocaleTimeString('de-DE', {
                hour: "numeric",
                minute: "numeric"
            }));

            if (index === -1) {
                availableTimes.value.push({
                    time: i.toLocaleTimeString('de-DE', {hour: "numeric", minute: "numeric"}),
                    disabled: true
                });
            }
        }
        availableTimes.value.sort(({time: a}, {time: b}) => a > b ? 1 : a < b ? -1 : 0);
    }

    async function loadEventOptions(eventId: number) {
        const {getAsync} = useCrud();
        try {
            const response = await getAsync(`/extern/table/geteventoptions/?store=${route.query.store}&eventid=${eventId}`);
            eventOptions.value = Array.isArray(response) ? response : [];
        } catch (e) {
            console.log(e);
            eventOptions.value = [];
        }
    }

    const eventOptions = ref([] as any[]);

    return {getTimes, getDays, getEventDays, loadEventOptions, availableTimes, availableDates, chosenTable, markers, eventsByDate, selectedEvent, eventOptions}
})
